import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/auth.store';
import { useRestaurantStore } from '@/stores/restaurant.store';
import { useRealtimeStream } from '@/hooks/useRealtimeStream';
import { realtimeStreamPath } from '@/lib/realtime-stream-path';

/**
 * Live order updates via Server-Sent Events. The backend streams a lightweight
 * `invalidate` signal whenever an order for the selected restaurant is created
 * or changed (a new storefront order, a customer cancellation, the stale-order
 * sweeper); we just invalidate the cache so the open Orders view refetches at
 * once - no manual page refresh.
 *
 * Mirrors useReservationsRealtime. Connection handling - auth header, token
 * refresh on 401, reconnect - lives in useRealtimeStream.
 */
export function useOrdersRealtime() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const queryClient = useQueryClient();

  const onEvent = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['orders'] });
  }, [queryClient]);

  useRealtimeStream({
    path: restaurantId ? realtimeStreamPath(restaurantId, 'orders') : null,
    label: 'orders',
    enabled: isAuthenticated,
    onEvent,
  });
}
